import React, { useContext } from 'react';
import {
  Container,
  InnerContainer,
  Heading,
  RowItem,
  Row,
  ErrorMessage,
  Footer,
  CancelButton,
  ButtonContainer,
  Loader,
  UploadContainer,
  Item,
} from '@app/modules/profile/profile-components';
import { Input, Button, Toast, Icon } from '@app/components';
import { useForm } from 'react-hook-form';
import Uploader from '@app/components/uploader';
import { useLocation } from '@app/utils';
import { AuthContext } from '@app/providers';
import { useCreateUserMutation, useGetUserQuery } from '@app/graphql';
import { getDefaultUrl } from '@app/routes/routes-helper';
import DefaultErrorPage from '@app/components/error-pages/default-error-page';

export default function Profile() {
  const { register, handleSubmit, errors } = useForm();
  const { navigate } = useLocation();
  const { userInfo, setUserInfo }: any = useContext(AuthContext);
  const defaultUrl: string = getDefaultUrl(userInfo);
  const { loading, data } = useGetUserQuery({ firebaseId: userInfo.id });
  const [updateUserDB] = useCreateUserMutation();
  const [userData, updateUserData] = React.useState({
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    email: userInfo.email,
    phone: userInfo.phone,
    company: userInfo.company || '',
    roleData: { _id: '', name: '', key: '', status: '' },
    userTypeData: { _id: '', name: '', key: '', status: '' },
  });
  const [editing, updateEdittingValue] = React.useState(false);

  const [fileName, updateFileName] = React.useState('');
  const [profileImage, updateImage] = React.useState('');
  const [showToast, setShowToast] = React.useState(false);
  const [showErrorPage, SetShowErrorPage] = React.useState(false);
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const [phoneNumber, updatePhoneNumber] = React.useState(userInfo.phone);
  const [userLoading, setUserLoading] = React.useState(true);
  React.useEffect(() => {
    if (
      !loading &&
      data &&
      data.user &&
      data.user.profilePicUrl !== profileImage &&
      !editing
    ) {
      updateUserData({ ...userData, ...data.user });
      updateImage(data.user.profilePicUrl);
      updateFileName(data.user.profilePicUrl);
    }
    if (!loading && data && data.user) {
      const { user } = data;
      if (user.phone) {
        updatePhoneNumber(user.phone);
      }
      setTimeout(() => {
        setUserLoading(false);
      }, 500);
    }
    // eslint-disable-next-line
  }, [loading, data, userData, editing]);

  const ChoosePic = (event: any) => {
    updateEdittingValue(true);
    if (event.target.files && event.target.files[0]) {
      updateFileName(event.target.files[0].name);

      let reader = new FileReader();

      reader.onload = (e: any) => {
        updateImage(e.target.result);
      };

      reader.readAsDataURL(event.target.files[0]);
    }
  };

  function onSubmit(data: any) {
    const { roleData, userTypeData } = userData;
    updateEdittingValue(false);
    setUserInfo({
      ...userInfo,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: phoneNumber,
      company: data.company,
      profilePicUrl: profileImage,
    });
    updateImage(profileImage);
    updateUserDB({
      variables: {
        action: 'edit',
        firebaseId: userInfo.firebaseId || userInfo.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: phoneNumber,
        company: data.company,
        profilePicUrl: profileImage,
        source: userInfo.source,
        userTypeId: userTypeData && userTypeData._id ? userTypeData._id : '',
        userRoleId: roleData && roleData._id ? roleData._id : '',
      },
    })
      .then((res: any) => {
        setShowToast(true);
        setShowToast(false);
      })
      .catch(err => {
        SetShowErrorPage(true);
      });
  }

  function onChange(event: any) {
    if (event.target.name === 'firstName') {
      updateUserData({ ...userData, firstName: event.target.value });
    }
    if (event.target.name === 'lastName') {
      updateUserData({ ...userData, lastName: event.target.value });
    }
    if (event.target.name === 'email') {
      updateUserData({ ...userData, email: event.target.value });
    }
    if (event.target.name === 'company') {
      updateUserData({ ...userData, company: event.target.value });
    }
  }

  const deleteImage = () => {
    updateFileName('');
    updateImage('');
  };

  const onNumberChange = (e: any) => {
    updatePhoneNumber(e);
  };

  return (
    <>
      <Toast
        message="Profile Updated Successfully"
        showToast={showToast}
        onClose={() => setShowToast(false)}
      />
      {loading || userLoading ? (
        <Loader>
          <Icon name="loader" />
        </Loader>
      ) : showErrorPage ? (
        <DefaultErrorPage />
      ) : (
        <Container>
          <InnerContainer>
            <Heading>My Profile</Heading>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <UploadContainer>
                <Uploader
                  isProfile={true}
                  onChange={e => ChoosePic(e)}
                  fileName={fileName}
                  borderRadius="6px"
                  profileImage={profileImage}
                  onImageDelete={deleteImage}
                />
              </UploadContainer>
              <Row style={{ marginTop: '20px' }}>
                <RowItem>
                  <Input
                    label="First Name"
                    placeholder="Enter First Name"
                    name="firstName"
                    value={userData.firstName || ''}
                    onChange={onChange}
                    register={register({ required: true })}
                  />
                  {errors.firstName && (
                    <ErrorMessage>Please enter first name</ErrorMessage>
                  )}
                </RowItem>
                <RowItem>
                  <Input
                    label="Last Name"
                    placeholder="Enter Last Name"
                    name="lastName"
                    value={userData.lastName || ''}
                    onChange={onChange}
                    register={register({ required: true })}
                  />
                  {errors.lastName && (
                    <ErrorMessage>Please enter last name</ErrorMessage>
                  )}
                </RowItem>
              </Row>
              <Row>
                <RowItem>
                  <Input
                    type="email"
                    label="Email"
                    readOnly
                    placeholder="Enter Email"
                    name="email"
                    value={userData.email || ''}
                    onChange={onChange}
                    register={register({
                      required: true,
                      pattern: emailPattern,
                    })}
                  />
                  {errors.email && errors.email.type === 'required' ? (
                    <ErrorMessage>Please enter email</ErrorMessage>
                  ) : null}
                  {errors.email && errors.email.type === 'pattern' && (
                    <ErrorMessage>Please enter valid email</ErrorMessage>
                  )}
                </RowItem>
                <RowItem>
                  <Input
                    type="phone_number"
                    label="Phone"
                    placeholder="Enter Phone Number"
                    name="phone"
                    onChange={onNumberChange}
                    value={
                      (phoneNumber && phoneNumber.startsWith('+1')
                        ? phoneNumber.substring(2)
                        : phoneNumber) || ''
                    }
                    register={register({
                      pattern: /^[0-9]*/,
                    })}
                  />
                </RowItem>
              </Row>
              <Row>
                <Item>
                  <Input
                    label="Company"
                    placeholder="Enter Company Name"
                    name="company"
                    value={userData.company || ''}
                    onChange={onChange}
                    register={register()}
                  />
                </Item>
              </Row>
              <Footer>
                <CancelButton onClick={() => navigate(defaultUrl)}>
                  Cancel
                </CancelButton>
                <ButtonContainer>
                  <Button
                    variant="warning"
                    size="large"
                    width="100%"
                    type="submit">
                    Save & Update
                  </Button>
                </ButtonContainer>
              </Footer>
            </form>
          </InnerContainer>
        </Container>
      )}
    </>
  );
}
